/** Theme V2 feature switches that are intentionally controlled in code. */
export const THEME_V2_FEATURE_FLAGS = Object.freeze({
  propagateThemeChangesToSelectedTemplates: false,
});

/**
 * Browser-only E2E opt-in. It is ignored unless the existing Theme V2 eval
 * hook is also enabled, so ordinary application sessions always use the
 * source-controlled flag above.
 */
export const THEME_V2_SELECTED_TEMPLATE_PROPAGATION_E2E_STORAGE_KEY =
  'theme-v2-selected-template-propagation-e2e';

interface ThemeV2FeatureFlagStorage {
  getItem(key: string): string | null;
}

/**
 * Resolve the selected-template propagation gate for the current host.
 *
 * The checked-in code flag remains the product source of truth and defaults to
 * Off. The localStorage branch is an explicit browser-test harness layered on
 * top of the already opt-in `theme-v2-eval` mode; it never changes persisted
 * Theme data and is not exposed as product UI.
 */
export function isThemeV2SelectedTemplatePropagationEnabled(
  storage?: ThemeV2FeatureFlagStorage | null,
): boolean {
  if (THEME_V2_FEATURE_FLAGS.propagateThemeChangesToSelectedTemplates) {
    return true;
  }

  const runtimeStorage = storage ?? (
    typeof window !== 'undefined' ? window.localStorage : null
  );
  if (!runtimeStorage) return false;

  try {
    return runtimeStorage.getItem('theme-v2-eval') === '1'
      && runtimeStorage.getItem(
        THEME_V2_SELECTED_TEMPLATE_PROPAGATION_E2E_STORAGE_KEY,
      ) === '1';
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
    return false;
  }
}
